/**
 * Janko Piano - MIDI Module
 * Handles Web MIDI API for input/output with auto-detection
 * @version 0.2.66
 */

import { CONFIG } from './config.js';
import { playNote, stopNote, engageSustain, releaseSustain } from './audio.js';
import { setReverb, setChorus } from './ui.js';

// =============================================================================
// MIDI STATE
// =============================================================================

let midiAccess = null;
let selectedInputDevice = null;
let selectedOutputDevice = null;
let midiInputEnabled = false;
let midiOutputEnabled = false;

// Device lists
let inputDevices = [];
let outputDevices = [];

// Track active MIDI notes (for note off handling)
const activeMIDINotes = new Map(); // midiNote -> noteData

// =============================================================================
// MIDI MESSAGE CONSTANTS
// =============================================================================

const MIDI_NOTE_ON = 0x90;
const MIDI_NOTE_OFF = 0x80;
const MIDI_CONTROL_CHANGE = 0xB0;
const MIDI_PROGRAM_CHANGE = 0xC0;

// Control Change Numbers
const CC_SUSTAIN_PEDAL = 64;
const CC_REVERB = 91;
const CC_CHORUS = 93;

// =============================================================================
// MIDI INITIALIZATION
// =============================================================================

export async function initMIDI() {
    if (!navigator.requestMIDIAccess) {
        console.warn('[MIDI] Web MIDI API not supported in this browser');
        return false;
    }
    
    try {
        midiAccess = await navigator.requestMIDIAccess({ sysex: false });
        console.log('[MIDI] Access granted');
        
        // List devices
        updateDeviceLists();
        
        // Listen for device connect/disconnect
        midiAccess.onstatechange = onMIDIStateChange;
        
        // Load saved settings
        loadMIDISettings();
        
        return true;
    } catch (err) {
        console.error('[MIDI] Access denied:', err);
        return false;
    }
}

// =============================================================================
// DEVICE MANAGEMENT
// =============================================================================

function updateDeviceLists() {
    inputDevices = [];
    outputDevices = [];
    
    // Enumerate inputs
    for (const [id, input] of midiAccess.inputs) {
        inputDevices.push({
            id: id,
            name: input.name || 'Unknown Device',
            manufacturer: input.manufacturer || 'Unknown',
            state: input.state,
            port: input
        });
    }
    
    // Enumerate outputs
    for (const [id, output] of midiAccess.outputs) {
        outputDevices.push({
            id: id,
            name: output.name || 'Unknown Device',
            manufacturer: output.manufacturer || 'Unknown',
            state: output.state,
            port: output
        });
    }
    
    console.log(`[MIDI] Found ${inputDevices.length} input(s), ${outputDevices.length} output(s)`);
    
    // Update UI
    if (window.updateMIDIDeviceUI) {
        window.updateMIDIDeviceUI();
    }
}

function onMIDIStateChange(event) {
    console.log(`[MIDI] Device ${event.port.state}: ${event.port.name}`);
    const deviceName = event.port.name || 'Unknown Device';
    
    updateDeviceLists();
    
    // If current device disconnected, disable it
    if (event.port.state === 'disconnected') {
        if (selectedInputDevice && selectedInputDevice.id === event.port.id) {
            disableMIDIInput();
            showMIDIToast(`❌ MIDI Input Disconnected: ${deviceName}`);
        }
        if (selectedOutputDevice && selectedOutputDevice.id === event.port.id) {
            disableMIDIOutput();
            showMIDIToast(`❌ MIDI Output Disconnected: ${deviceName}`);
        }
    }
    
    // Auto-select when device connects (all platforms)
    if (event.port.state === 'connected') {
        autoSelectDevices(event.port);
    }
}

// Helper: Check if running on Android
function isAndroid() {
    return CONFIG.detectedType === 'android' || 
           (typeof window.Capacitor !== 'undefined' && window.Capacitor.getPlatform() === 'android');
}

// Smart device role detection based on name patterns
function detectDeviceRole(deviceName) {
    const name = deviceName.toLowerCase();
    
    // Input-only devices (controllers, keyboards that send MIDI)
    const inputOnlyPatterns = [
        'keyboard', 'controller', 'pad', 'launchpad', 'akai', 'novation',
        'push', 'maschine', 'mpk', 'oxygen', 'keystation', 'arturia'
    ];
    
    // Output-only devices (synthesizers, sound modules that receive MIDI)
    const outputOnlyPatterns = [
        'synth', 'module', 'sound', 'tone generator', 'expander',
        'usb peripheral' // Android virtual MIDI output
    ];
    
    // Check for input-only patterns
    for (const pattern of inputOnlyPatterns) {
        if (name.includes(pattern)) {
            return 'input-only';
        }
    }
    
    // Check for output-only patterns
    for (const pattern of outputOnlyPatterns) {
        if (name.includes(pattern)) {
            return 'output-only';
        }
    }
    
    // Default: device can be both (e.g., digital piano, workstation)
    return 'both';
}

// Universal auto-selection logic for all platforms
function autoSelectDevices(connectedPort) {
    const isInput = connectedPort.type === 'input';
    const isOutput = connectedPort.type === 'output';
    const deviceName = connectedPort.name || 'Unknown Device';
    const deviceRole = detectDeviceRole(deviceName);
    
    console.log(`[MIDI] Device role detected: ${deviceRole} for "${deviceName}"`);
    
    // Auto-select input device
    if (isInput && !selectedInputDevice) {
        // Only auto-select as input if device is input-capable
        if (deviceRole === 'input-only' || deviceRole === 'both') {
            const device = inputDevices.find(d => d.id === connectedPort.id);
            if (device) {
                console.log(`[MIDI] Auto-selecting input: ${deviceName}`);
                enableMIDIInput(device.id);
                showMIDIToast(`🎹 MIDI Input Connected: ${deviceName}`);
            }
        } else {
            console.log(`[MIDI] Skipping input auto-select for output-only device: ${deviceName}`);
        }
    }
    
    // Auto-select output device
    if (isOutput && !selectedOutputDevice) {
        // Only auto-select as output if device is output-capable
        if (deviceRole === 'output-only' || deviceRole === 'both') {
            // Android: Prefer "USB Peripheral" port
            if (isAndroid()) {
                const usbPeripheral = outputDevices.find(d => 
                    d.name.toLowerCase().includes('usb') && 
                    d.name.toLowerCase().includes('peripheral')
                );
                
                if (usbPeripheral) {
                    console.log('[MIDI] Android: Auto-selecting USB Peripheral output');
                    enableMIDIOutput(usbPeripheral.id);
                    showMIDIToast(`🎵 MIDI Output Connected: ${usbPeripheral.name}`);
                    return;
                }
            }
            
            // All platforms: Auto-select the newly connected output
            const device = outputDevices.find(d => d.id === connectedPort.id);
            if (device) {
                console.log(`[MIDI] Auto-selecting output: ${deviceName}`);
                enableMIDIOutput(device.id);
                showMIDIToast(`🎵 MIDI Output Connected: ${deviceName}`);
            }
        } else {
            console.log(`[MIDI] Skipping output auto-select for input-only device: ${deviceName}`);
        }
    }
}

// Show toast notification for MIDI events
function showMIDIToast(message) {
    if (window.showToast) {
        window.showToast(message);
    } else {
        console.log(`[MIDI Toast] ${message}`);
    }
}

export function getInputDevices() {
    return inputDevices;
}

export function getOutputDevices() {
    return outputDevices;
}

export function refreshDevices() {
    if (midiAccess) {
        updateDeviceLists();
    }
}

// =============================================================================
// MIDI INPUT
// =============================================================================

export function enableMIDIInput(deviceId) {
    if (!midiAccess) {
        console.warn('[MIDI] MIDI not initialized');
        return false;
    }
    
    // Disable previous device
    if (selectedInputDevice) {
        selectedInputDevice.port.onmidimessage = null;
    }
    
    // Find and enable new device
    const device = inputDevices.find(d => d.id === deviceId);
    if (!device) {
        console.warn('[MIDI] Input device not found:', deviceId);
        return false;
    }
    
    selectedInputDevice = device;
    device.port.onmidimessage = onMIDIMessage;
    midiInputEnabled = true;
    
    CONFIG.midiInputEnabled = true;
    CONFIG.midiInputDeviceId = deviceId;
    saveMIDISettings();
    
    console.log(`[MIDI] Input enabled: ${device.name}`);
    
    if (window.updateMIDIStatusIndicators) {
        window.updateMIDIStatusIndicators();
    }
    
    return true;
}

export function disableMIDIInput() {
    if (selectedInputDevice) {
        selectedInputDevice.port.onmidimessage = null;
        selectedInputDevice = null;
    }
    
    midiInputEnabled = false;
    CONFIG.midiInputEnabled = false;
    CONFIG.midiInputDeviceId = null;
    saveMIDISettings();
    
    // Release all active MIDI notes
    activeMIDINotes.forEach((noteData, midiNote) => {
        stopNote(noteData);
    });
    activeMIDINotes.clear();
    
    console.log('[MIDI] Input disabled');
    
    if (window.updateMIDIStatusIndicators) {
        window.updateMIDIStatusIndicators();
    }
}

function onMIDIMessage(event) {
    const [status, data1, data2] = event.data;
    const command = status & 0xF0;
    const channel = status & 0x0F;
    
    switch (command) {
        case MIDI_NOTE_ON:
            if (data2 > 0) { // velocity > 0
                handleMIDINoteOn(data1, data2);
            } else {
                handleMIDINoteOff(data1);
            }
            break;
            
        case MIDI_NOTE_OFF:
            handleMIDINoteOff(data1);
            break;
            
        case MIDI_CONTROL_CHANGE:
            handleMIDIControlChange(data1, data2);
            break;
            
        case MIDI_PROGRAM_CHANGE:
            console.log(`[MIDI] Program Change: ${data1}`);
            break;
    }
}

function handleMIDINoteOn(midiNote, velocity) {
    // Play audio
    const noteData = playNote(midiNote);
    
    if (noteData) {
        activeMIDINotes.set(midiNote, noteData);
        
        // Visual feedback - highlight key
        if (window.highlightMIDIKey) {
            window.highlightMIDIKey(midiNote);
        }
    }
    
    // Recording (MIDI input is already recorded via audio.js playNote hook)
    // No need to record again here to avoid duplicates
}

function handleMIDINoteOff(midiNote) {
    const noteData = activeMIDINotes.get(midiNote);
    
    if (noteData) {
        stopNote(noteData);
        activeMIDINotes.delete(midiNote);
        
        // Remove visual feedback
        if (window.unhighlightMIDIKey) {
            window.unhighlightMIDIKey(midiNote);
        }
    }
    
    // Recording (MIDI input is already recorded via audio.js stopNote hook)
    // No need to record again here to avoid duplicates
}

function handleMIDIControlChange(ccNumber, value) {
    switch (ccNumber) {
        case CC_SUSTAIN_PEDAL:
            // value >= 64 = pedal down
            if (value >= 64) {
                engageSustain();
                console.log('[MIDI] Sustain: ON');
            } else {
                releaseSustain();
                console.log('[MIDI] Sustain: OFF');
            }
            break;
            
        case CC_REVERB:
            setReverb(value / 127);
            break;
            
        case CC_CHORUS:
            setChorus(value / 127);
            break;
            
        default:
            // Log other CC messages for debugging
            if (CONFIG.debugMode) {
                console.log(`[MIDI] CC ${ccNumber}: ${value}`);
            }
            break;
    }
}

// =============================================================================
// MIDI OUTPUT
// =============================================================================

export function enableMIDIOutput(deviceId) {
    if (!midiAccess) {
        console.warn('[MIDI] MIDI not initialized');
        return false;
    }
    
    const device = outputDevices.find(d => d.id === deviceId);
    if (!device) {
        console.warn('[MIDI] Output device not found:', deviceId);
        return false;
    }
    
    selectedOutputDevice = device;
    midiOutputEnabled = true;
    
    CONFIG.midiOutputEnabled = true;
    CONFIG.midiOutputDeviceId = deviceId;
    saveMIDISettings();
    
    console.log(`[MIDI] Output enabled: ${device.name}`);
    
    if (window.updateMIDIStatusIndicators) {
        window.updateMIDIStatusIndicators();
    }
    
    return true;
}

export function disableMIDIOutput() {
    if (selectedOutputDevice) {
        // Send all notes off
        sendAllNotesOff();
        selectedOutputDevice = null;
    }
    
    midiOutputEnabled = false;
    CONFIG.midiOutputEnabled = false;
    CONFIG.midiOutputDeviceId = null;
    saveMIDISettings();
    
    console.log('[MIDI] Output disabled');
    
    if (window.updateMIDIStatusIndicators) {
        window.updateMIDIStatusIndicators();
    }
}

export function sendMIDINoteOn(midiNote, velocity = 127, channel = 0) {
    if (!midiOutputEnabled || !selectedOutputDevice) return;
    
    try {
        const message = [MIDI_NOTE_ON | channel, midiNote, velocity];
        selectedOutputDevice.port.send(message);
    } catch (err) {
        console.error('[MIDI] Failed to send note on:', err);
    }
}

export function sendMIDINoteOff(midiNote, channel = 0) {
    if (!midiOutputEnabled || !selectedOutputDevice) return;
    
    try {
        const message = [MIDI_NOTE_OFF | channel, midiNote, 0];
        selectedOutputDevice.port.send(message);
    } catch (err) {
        console.error('[MIDI] Failed to send note off:', err);
    }
}

export function sendMIDIControlChange(ccNumber, value, channel = 0) {
    if (!midiOutputEnabled || !selectedOutputDevice) return;
    
    try {
        const message = [MIDI_CONTROL_CHANGE | channel, ccNumber, Math.round(value)];
        selectedOutputDevice.port.send(message);
    } catch (err) {
        console.error('[MIDI] Failed to send CC:', err);
    }
}

function sendAllNotesOff(channel = 0) {
    if (!midiOutputEnabled || !selectedOutputDevice) return;
    
    try {
        // Send CC 123 (All Notes Off)
        const message = [MIDI_CONTROL_CHANGE | channel, 123, 0];
        selectedOutputDevice.port.send(message);
    } catch (err) {
        console.error('[MIDI] Failed to send all notes off:', err);
    }
}

// =============================================================================
// SETTINGS PERSISTENCE
// =============================================================================

function saveMIDISettings() {
    try {
        localStorage.setItem('midiInputDeviceId', CONFIG.midiInputDeviceId || '');
        localStorage.setItem('midiOutputDeviceId', CONFIG.midiOutputDeviceId || '');
        localStorage.setItem('midiInputEnabled', CONFIG.midiInputEnabled);
        localStorage.setItem('midiOutputEnabled', CONFIG.midiOutputEnabled);
    } catch (err) {
        console.warn('[MIDI] Failed to save settings:', err);
    }
}

function loadMIDISettings() {
    try {
        CONFIG.midiInputDeviceId = localStorage.getItem('midiInputDeviceId') || null;
        CONFIG.midiOutputDeviceId = localStorage.getItem('midiOutputDeviceId') || null;
        CONFIG.midiInputEnabled = localStorage.getItem('midiInputEnabled') === 'true';
        CONFIG.midiOutputEnabled = localStorage.getItem('midiOutputEnabled') === 'true';
        
        // Reconnect to saved devices if available
        if (CONFIG.midiInputEnabled && CONFIG.midiInputDeviceId) {
            setTimeout(() => {
                const device = inputDevices.find(d => d.id === CONFIG.midiInputDeviceId);
                if (device) {
                    enableMIDIInput(CONFIG.midiInputDeviceId);
                }
            }, 100);
        }
        
        if (CONFIG.midiOutputEnabled && CONFIG.midiOutputDeviceId) {
            setTimeout(() => {
                const device = outputDevices.find(d => d.id === CONFIG.midiOutputDeviceId);
                if (device) {
                    enableMIDIOutput(CONFIG.midiOutputDeviceId);
                }
            }, 100);
        }
    } catch (err) {
        console.warn('[MIDI] Failed to load settings:', err);
    }
}

// =============================================================================
// GETTERS
// =============================================================================

export function isMIDISupported() {
    return !!navigator.requestMIDIAccess;
}

export function isMIDIInputEnabled() {
    return midiInputEnabled;
}

export function isMIDIOutputEnabled() {
    return midiOutputEnabled;
}

export function getSelectedInputDevice() {
    return selectedInputDevice;
}

export function getSelectedOutputDevice() {
    return selectedOutputDevice;
}
